import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, CreditCard, Loader2, X, QrCode } from "lucide-react";
import { toast } from "sonner";

const remainingText = (expired) => {
  if (!expired) return "";
  try {
    const end = new Date(String(expired).replace(" ", "T")).getTime();
    const diff = end - Date.now();
    if (diff <= 0) return "Kadaluarsa";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h} jam ${m} menit lagi` : `${m} menit lagi`;
  } catch {
    return "";
  }
};

export function PendingPaymentBanner({ onState }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/pending");
      setPending(data.pending);
      onState && onState(data.pending);
    } catch {
      setPending(null);
      onState && onState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = async () => {
    if (!pending) return;
    setCancelling(true);
    try {
      await api.post(`/payments/${pending.session_id}/cancel`);
      toast.success("Kode bayar dibatalkan. Anda bisa membuat pembayaran baru.");
      setConfirmOpen(false);
      setPending(null);
      onState && onState(null);
    } catch {
      toast.error("Gagal membatalkan kode bayar. Coba lagi.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading || !pending) return null;

  const p = pending.payment || {};
  const isQr = !!(p.qr_string || p.qr_image);
  const methodLabel = isQr
    ? "QRIS"
    : p.payment_no
    ? `Virtual Account ${p.channel || ""}`.trim()
    : "iPaymu";
  const rem = remainingText(pending.expired);

  return (
    <div
      data-testid="pending-payment-banner"
      className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5 shadow-sm animate-fade-up"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
            {isQr ? <QrCode size={20} className="text-amber-600" /> : <CreditCard size={20} className="text-amber-600" />}
          </div>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-stone-900 flex items-center gap-2 flex-wrap">
              Menunggu Pembayaran
              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                Belum dibayar
              </span>
            </p>
            <p className="text-sm text-stone-600 mt-0.5 truncate">
              {pending.description || "Pembayaran"} · <span className="font-mono font-bold text-stone-900">{rupiah(pending.amount)}</span>
            </p>
            <p className="text-xs text-stone-500 mt-1">
              {methodLabel}
              {!isQr && p.payment_no ? <> · <span className="font-mono font-semibold text-stone-700">{p.payment_no}</span></> : null}
            </p>
            {rem && (
              <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                <Clock size={12} /> {rem === "Kadaluarsa" ? "Kode bayar kadaluarsa" : `Bayar sebelum ${rem}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={() => navigate(`/payment/instructions?session_id=${pending.session_id}`)}
            data-testid="pending-view-code-btn"
            className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
          >
            Lihat Kode Bayar
          </Button>
          {!confirmOpen ? (
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              data-testid="pending-cancel-btn"
              className="rounded-xl border-stone-300 text-stone-600"
            >
              <X size={15} className="mr-1" /> Batalkan
            </Button>
          ) : (
            <div className="flex items-center gap-1.5" data-testid="pending-cancel-confirm">
              <Button
                variant="ghost"
                onClick={() => setConfirmOpen(false)}
                disabled={cancelling}
                className="rounded-xl text-stone-500"
              >
                Kembali
              </Button>
              <Button
                onClick={cancel}
                disabled={cancelling}
                data-testid="pending-cancel-confirm-btn"
                className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white"
              >
                {cancelling ? <Loader2 size={15} className="animate-spin" /> : "Ya, Batalkan"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
